import {
	ChangeEvent,
	createElement,
	FocusEvent,
	KeyboardEvent,
	MouseEvent,
	ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react';
import MaskedInput from 'react-maskedinput';
import classNames from 'classnames';

import { copyToClipboard } from '@/components/input/Constants';
import { DEVICE_TYPE, FRONTEND_URL } from '@/services/Constants';

import Spinner from '@/components/spinner/Spinner';

interface FormInputProps {
	label: string;
	onChange: (value: string) => void;
	actionNode?: ReactNode;
	autoComplete?: string;
	classname?: string;
	color?: 'gray';
	dataPrivate?: boolean;
	deletable?: boolean;
	disabled?: boolean;
	disabledError?: boolean;
	error?: string | boolean;
	floatingLabel?: boolean;
	focusOnMount?: boolean;
	loading?: boolean;
	mask?: string;
	name?: string;
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
	onClick?: (e: MouseEvent<HTMLInputElement>) => void;
	onDelete?: (name: string) => void;
	onEdit?: (name: string) => void;
	onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
	onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
	readonly?: boolean;
	type?: 'text' | 'password' | 'email' | 'url' | 'editable' | 'delete' | 'copy' | 'number';
	value?: string | number;
}

const FormInput = ({
	actionNode,
	autoComplete,
	classname,
	color,
	dataPrivate = true,
	deletable,
	disabled = false,
	disabledError = false,
	error,
	floatingLabel = true,
	focusOnMount = false,
	label,
	loading,
	mask,
	name = '',
	onBlur = () => {},
	onChange = () => {},
	onClick,
	onDelete,
	onEdit = () => {},
	onFocus = () => {},
	onKeyDown = () => {},
	readonly = false,
	type = 'text',
	value,
}: FormInputProps) => {
	const [showValue, setShowValueStatus] = useState<boolean>(false);
	const inputField = useRef<HTMLInputElement>(null);
	const handleChange = (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value);
	const showHideValue = () => setShowValueStatus(!showValue);
	const handleOnEdit = () => onEdit(name);
	const handleCopy = () => copyToClipboard(inputField, type === 'url' ? 'link' : 'copy');
	const handleDelete = () => onDelete && onDelete(name);
	const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => onKeyDown(e);

	let activeState = false;

	const handleOnFocus = (e: FocusEvent<HTMLInputElement>) => {
		activeState = true;
		DEVICE_TYPE.type === 'ios' && window.dispatchEvent(new Event('keyboardDidShow'));
		onFocus(e);
	};

	const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
		activeState = false;
		DEVICE_TYPE.type === 'ios' && window.dispatchEvent(new Event('keyboardDidHide'));
		onBlur(e);
	};

	useEffect(() => {
		focusOnMount && inputField.current?.focus();

		return () => {
			DEVICE_TYPE.type === 'ios' &&
				activeState &&
				window.dispatchEvent(new Event('keyboardDidHide'));
		};
	}, [focusOnMount]);

	return (
		<div
			className={classNames('form-field', classname, {
				'copy-field': type === 'url' || type === 'copy',
			})}
		>
			<div
				className={classNames('input-field notranslate', {
					error,
					'hide-placeholder': floatingLabel,
					'hide-label': !floatingLabel,
				})}
			>
				{type === 'url' && <span className="base-url prefilled-val">{FRONTEND_URL}/</span>}
				{createElement(mask ? MaskedInput : 'input', {
					autoComplete: autoComplete ?? '',
					placeholder: floatingLabel ? '*' : label,
					className: classNames({
						error,
						'light-gray': color === 'gray',
						'hide-label': !floatingLabel,
					}),
					name,
					onFocus: handleOnFocus,
					onBlur: handleOnBlur,
					onChange: handleChange,
					onKeyDown: handleOnKeyDown,
					onClick,
					type:
						(showValue && type === 'password') ||
						type === 'editable' ||
						type === 'delete' ||
						type === 'copy'
							? 'text'
							: type === 'url'
							? 'text'
							: type,
					value: value || '',
					readOnly: readonly,
					disabled,
					ref: inputField,
					'data-name': name,
					'data-testid': name,
					mask,
					'data-private': dataPrivate,
				})}

				{floatingLabel && <label>{label}</label>}
				{type === 'password' && value && !loading && (
					<button
						type="button"
						className="btn-icon-link field-btn"
						onClick={showHideValue}
					>
						<span
							className={classNames(
								'icon',
								`icon-visibility${!showValue ? '-off' : ''}`,
							)}
						/>
					</button>
				)}
				{type === 'editable' && !loading && (
					<button
						type="button"
						className="btn-icon-link field-btn"
						onClick={handleOnEdit}
					>
						<span className="icon icon-edit" />
					</button>
				)}
				{(type === 'url' || type === 'copy') && !loading && (
					<button type="button" className="btn-icon-link field-btn" onClick={handleCopy}>
						<span className="icon icon-copy" />
					</button>
				)}
				{(type === 'delete' || deletable) && !loading && (
					<button
						type="button"
						className="btn-icon-link field-btn"
						tabIndex={-1}
						onClick={handleDelete}
					>
						<span className="icon icon-trash-can" />
					</button>
				)}
				{actionNode}
				{loading && <Spinner />}
			</div>
			{!disabledError && error && (
				<span className="error-msg" data-name={name}>
					{error}
				</span>
			)}
		</div>
	);
};

export default FormInput;
